import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Icon, Popover } from '@syneto/compass-react';
import InfoIcon from '../../../../../components/Form/InfoIcon';
import { useExpiringInterval } from '../../../../../hooks/useExpiringInterval';
import { VALIDITY_STATUS, formatCalendarDate, getMaintenanceExpiry } from '../../../../../helpers/ExpiryRule';

export const NO_MAINTENANCE_INFORMATION_TEXT = 'No maintenance information for the machines of this location.';

const formatExpiresIn = (daysLeft) => {
	if (daysLeft === 0) {
		return 'today';
	}

	return `in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`;
};

const renderMachineName = (machine) => {
	return <strong>{machine.name || machine.serialNumber}&nbsp;</strong>;
};

// One row per machine whose maintenance has an end, soonest first, with the same expiry rule as the Inventory list
// and the machine pane (getMaintenanceExpiry), so the three agree on when a maintenance ends. A machine the batch
// did not return, or one without maintenance or dates, has no row.
const useMaintenanceExpiries = (machines, expiringInterval) => {
	return useMemo(() => {
		return machines
			.map((machine) => {
				return { machine, expiry: getMaintenanceExpiry(machine.maintenance?.expiresOn, expiringInterval) };
			})
			.filter(({ expiry }) => { return expiry !== null; })
			.sort((a, b) => { return a.expiry.calendarDate.localeCompare(b.expiry.calendarDate); });
	}, [machines, expiringInterval]);
};

export const MaintenanceStatus = (props) => {
	const { machines } = props;
	// null until the company setting resolves: nothing is called expiring before the threshold is known.
	const expiringInterval = useExpiringInterval();
	const expiries = useMaintenanceExpiries(machines, expiringInterval);

	const expired = expiries.filter(({ expiry }) => { return expiry.status === VALIDITY_STATUS.EXPIRED; });
	const expiring = expiries.filter(({ expiry }) => { return expiry.status === VALIDITY_STATUS.EXPIRING; });

	if (expired.length) {
		return (
			<div className="location-card-status-row">
				{expired.length === 1 ? '1 Machine expired' : `${expired.length} Machines expired`}

				<Popover
					trigger="click"
					placement="top"
					body={
						<>
							{expired.map(({ machine, expiry }) => {
								return (
									<div key={machine.id} className="location-card-popover-container">
										{renderMachineName(machine)}
										<span>|&nbsp;expired on&nbsp;{formatCalendarDate(expiry.calendarDate)}</span>
									</div>
								);
							})}
						</>
					}
					width={300}
					className="location-popover"
				>
					<span>
						<Icon
							className="pt-1 ms-2"
							name="fas fa-times-circle"
							color="#E45664"
							size="lg"
							style={{ cursor: 'pointer' }}
						/>
					</span>
				</Popover>
			</div>
		);
	}

	if (expiring.length) {
		return (
			<div className="location-card-status-row">
				{expiring.length === 1 ? '1 Machine expiring' : `${expiring.length} Machines expiring`}

				<Popover
					trigger="click"
					placement="top"
					body={
						<>
							{expiring.map(({ machine, expiry }) => {
								return (
									<div key={machine.id} className="location-card-popover-container">
										{renderMachineName(machine)}
										<span>|&nbsp;expires&nbsp;{formatExpiresIn(expiry.daysLeft)}</span>
									</div>
								);
							})}
						</>
					}
					className="location-popover"
				>
					<span>
						<Icon
							className="pt-1 ms-2"
							name="fas fa-exclamation-triangle"
							color="#F4BE00"
							size="lg"
							style={{ cursor: 'pointer' }}
						/>
					</span>
				</Popover>
			</div>
		);
	}

	if (!expiries.length) {
		return (
			<span>
				N/A
				<InfoIcon placement="top" text={NO_MAINTENANCE_INFORMATION_TEXT} iconStyle="pt-1 ms-2" />
			</span>
		);
	}

	const [{ machine: firstExpiringMachine, expiry: firstExpiry }] = expiries;

	return (
		<div className="location-card-status-row">
			<span>Full support coverage</span>

			<Popover
				trigger="click"
				placement="top"
				body={
					<>
						<div className="location-card-popover-container">
							{renderMachineName(firstExpiringMachine)}
							<span>|&nbsp;expires on&nbsp;{formatCalendarDate(firstExpiry.calendarDate)}</span>
						</div>
					</>
				}
				width={300}
				className="location-popover"
			>
				<span>
					<Icon
						className="pt-1 ms-2"
						name="fas fa-check-circle"
						color="#00b170"
						size="lg"
						style={{ cursor: 'pointer' }}
					/>
				</span>
			</Popover>
		</div>
	);
};

MaintenanceStatus.propTypes = {
	machines: PropTypes.array.isRequired,
};
